/**
 * Utility functions for cadence-step group ordering in the Tasks Table.
 * Extracted so they can be unit-tested independently of the component.
 */

/**
 * Parse a step label such as '1', '2', '3a', '3b' into a [numericPart, alphaSuffix]
 * tuple used for sort comparisons. Unrecognised labels sort last.
 * @param {string} label
 * @returns {[number, string]}
 */
export const parseStepLabel = (label = '') => {
  const match = label.match(/^(\d+)([a-z]?)$/);
  if (!match) return [Infinity, ''];
  return [parseInt(match[1], 10), match[2] || ''];
};

/**
 * Compare two step label strings numerically, then by alpha suffix.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const compareStepLabels = (a, b) => {
  const [aNum, aSuf] = parseStepLabel(a);
  const [bNum, bSuf] = parseStepLabel(b);
  if (aNum !== bNum) return aNum - bNum;
  return aSuf.localeCompare(bSuf);
};

/**
 * Sort cadence groups alphabetically by cadence name, then by step label within
 * each cadence. The ungrouped (Non-Cadence Tasks) bucket always comes last.
 *
 * @param {Map<string, { cadenceName: string, steps: Map<string, { key: string, stepSortKey: string, tasks: Array }> }>} cadenceGroups
 * @param {{ key: string, label: string, tasks: Array }} ungrouped
 * @returns {Array} flat sorted array of group objects
 */
export const sortGroups = (cadenceGroups, ungrouped) => {
  const sortedCadences = [...cadenceGroups.entries()].sort(([, a], [, b]) =>
    a.cadenceName.localeCompare(b.cadenceName)
  );

  const sorted = [];
  sortedCadences.forEach(([, cadenceEntry]) => {
    const sortedSteps = [...cadenceEntry.steps.values()].sort((a, b) =>
      compareStepLabels(a.stepSortKey, b.stepSortKey)
    );
    sorted.push(...sortedSteps);
  });

  if (ungrouped.tasks.length > 0) {
    sorted.push(ungrouped);
  }

  return sorted;
};
